"use client";

import type { ReactNode } from "react";

const PJK = "'Plus Jakarta Sans', var(--font-plus-jakarta), sans-serif";

interface SelectionCardProps {
  /** Uppercase label above the title, e.g. "EXECUTION SPEED" */
  label: string;
  /** Main question / heading */
  title: string;
  children: ReactNode;
  onSkip?: () => void;
  skipLabel?: string;
  onConfirm?: () => void;
  confirmDisabled?: boolean;
}

export function ChoiceRow({
  label,
  prefix,
  selected,
  onClick,
}: {
  label: string;
  prefix?: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        padding: "16px 24px",
        borderRadius: 12,
        background: selected ? "rgba(134,53,246,0.12)" : "#2d2c2c",
        border: selected
          ? "1px solid #8635f6"
          : "1px solid transparent",
        cursor: "pointer",
        textAlign: "left",
        transition: "background 150ms ease, border-color 150ms ease",
      }}
      onMouseEnter={(e) => {
        if (!selected) {
          (e.currentTarget as HTMLButtonElement).style.background = "#353535";
        }
      }}
      onMouseLeave={(e) => {
        if (!selected) {
          (e.currentTarget as HTMLButtonElement).style.background = "#2d2c2c";
        }
      }}
    >
      {prefix && (
        <span
          style={{
            fontFamily: PJK,
            fontWeight: 500,
            fontSize: 18,
            lineHeight: "28px",
            color: "#8f8e8c",
            flexShrink: 0,
          }}
        >
          {prefix}
        </span>
      )}
      <span
        style={{
          fontFamily: PJK,
          fontWeight: 700,
          fontSize: 18,
          lineHeight: "28px",
          color: selected ? "#c4a1ff" : "#f2f0ed",
          transition: "color 150ms ease",
        }}
      >
        {label}
      </span>
    </button>
  );
}

export default function SelectionCard({
  label,
  title,
  children,
  onSkip,
  skipLabel = "SKIP",
  onConfirm,
  confirmDisabled = false,
}: SelectionCardProps) {
  return (
    <div
      style={{
        background: "#1e1e1e",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 16,
        boxShadow: "0px 25px 50px -12px rgba(0,0,0,0.4)",
        maxWidth: 720,
        width: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div style={{ padding: "20px 24px 16px" }}>
        <p
          style={{
            fontFamily: PJK,
            fontWeight: 700,
            fontSize: 10,
            lineHeight: "15px",
            color: "#9e79d2",
            letterSpacing: "2px",
            textTransform: "uppercase",
            margin: "0 0 6px 0",
          }}
        >
          {label}
        </p>
        <h3
          style={{
            fontFamily: PJK,
            fontWeight: 700,
            fontSize: 18,
            lineHeight: "28px",
            color: "#f2f0ed",
            margin: 0,
          }}
        >
          {title}
        </h3>
      </div>

      {/* Choice rows */}
      <div style={{ padding: "0 16px", display: "flex", flexDirection: "column", gap: 8 }}>
        {children}
      </div>

      {/* Footer */}
      <div
        style={{
          background: "#2d2c2c",
          padding: "16px 24px",
          marginTop: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          {onSkip && (
            <button
              onClick={onSkip}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontFamily: PJK,
                fontWeight: 700,
                fontSize: 12,
                lineHeight: "18px",
                letterSpacing: "0.6px",
                color: "rgba(242,240,237,0.5)",
                textTransform: "uppercase",
                padding: 0,
              }}
            >
              {skipLabel}
            </button>
          )}
        </div>

        {/* Confirm button */}
        <button
          onClick={onConfirm}
          disabled={confirmDisabled}
          style={{
            width: 40,
            height: 40,
            borderRadius: 14,
            background: confirmDisabled ? "rgba(134,53,246,0.3)" : "#8635f6",
            border: "none",
            cursor: confirmDisabled ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            transition: "background 150ms ease",
          }}
        >
          {/* Arrow / send icon */}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M3 8h10M9 4l4 4-4 4"
              stroke="#fff"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
